import { useLingui, Trans } from '@lingui/react/macro';
import Editor, { type Monaco } from '@monaco-editor/react';
import { Label, TextArea } from '@utima/ui';
import { useCallback } from 'react';

export interface HttpToolBodyEditorProps {
  example: string;
  instructions: string;
  onExampleChange: (value: string) => void;
  onInstructionsChange: (value: string) => void;
}

const EXAMPLE_PLACEHOLDER = `{
  // static value — always sent as-is
  "channel": "support",
  // comments tell the model how to fill each value
  "subject": "<short summary of the issue>",
  "priority": "low | medium | high based on urgency"
}`;

/**
 * Request-body editor. The example is annotated JSON (jsonc — comments allowed)
 * shown to the model verbatim; it generates the full body to match. Comments
 * mark static values and explain how to fill the rest.
 */
export function HttpToolBodyEditor({
  example,
  instructions,
  onExampleChange,
  onInstructionsChange,
}: HttpToolBodyEditorProps) {
  const { t } = useLingui();

  const handleMount = useCallback((_editor: unknown, monaco: Monaco) => {
    // Allow comments so the annotated example validates as jsonc.
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: true,
    });
  }, []);

  return (
    <div className='space-y-3'>
      <div className='space-y-1.5'>
        <Label>
          <Trans>Body instructions</Trans>
        </Label>
        <TextArea
          value={instructions}
          onChange={e => onInstructionsChange(e.target.value)}
          rows={2}
          placeholder={t`Overall guidance for generating the request body.`}
        />
      </div>

      <div className='space-y-1.5'>
        <Label>
          <Trans>Body example (annotated JSON, comments allowed)</Trans>
        </Label>
        <div className='overflow-hidden rounded-radius border border-border'>
          <Editor
            height='220px'
            defaultLanguage='json'
            value={example}
            onChange={value => onExampleChange(value ?? '')}
            onMount={handleMount}
            options={{
              minimap: { enabled: false },
              lineNumbers: 'off',
              folding: false,
              scrollBeyondLastLine: false,
              fontSize: 13,
            }}
          />
        </div>
        <p className='text-xs text-muted-fg'>
          <Trans>
            Static values are advisory here — the model is told to keep them
            fixed but is not forced to. Put guaranteed or secret values in
            headers or a static path/query param.
          </Trans>
        </p>
        {!example.trim() && (
          <button
            type='button'
            className='text-xs text-primary underline'
            onClick={() => onExampleChange(EXAMPLE_PLACEHOLDER)}
          >
            <Trans>Insert example</Trans>
          </button>
        )}
      </div>
    </div>
  );
}
